import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const plans = [
  {
    id: "nocode",
    title: "No code development",
    desc: "Get your website developed in Framer, Webflow, or WordPress.",
    prices: { EUR: "€1,850", USD: "$2,000", AED: "7,350 AED", AUD: "A$3,050" },
    included: [
      "Delivered in 1—2 weeks",
      "Framer, Webflow, or WordPress",
      "1—5 pages",
      "Componentized architecture",
      "On-page scroll animations",
      "**Unlimited revisions until you're satisfied**"
    ],
    extras: [
      { service: "Additional CMS Page", price: "$300", unit: "per page" },
      { service: "Advanced Interactions", price: "$600", unit: "flat fee" },
      { service: "SEO Optimization", price: "$400", unit: "one-time" },
      { service: "Third-party API Sync", price: "$800", unit: "per service" }
    ],
    btnText: "Develop my Website"
  },
  {
    id: "design",
    title: "Website Design and Dev",
    desc: "Get a modern and eye catching landing page design",
    prices: { EUR: "€4,650", USD: "$5,000", AED: "18,450 AED", AUD: "A$7,650" },
    included: [
      "Delivered in 2—3 weeks",
      "Landing page design",
      "1—10 pages",
      "UI library",
      "Interactive prototype",
      "Responsive variations",
      "**Unlimited revisions until you're satisfied**"
    ],
    extras: [
      { service: "Simple Page Add-on", price: "$300", unit: "per page" },
      { service: "Complex Page Add-on", price: "$1,000", unit: "per page" },
      { service: "Custom Animations", price: "$500", unit: "flat fee" },
      { service: "Lottie Integration", price: "$100", unit: "per file" }
    ],
    btnText: "Start My Website Design"
  },
  {
    id: "app",
    title: "App Development",
    desc: "Get your Web or Mobile app developed with custom code.",
    prices: { EUR: "€9,999+", USD: "$10,800+", AED: "39,700+ AED", AUD: "A$16,500+" },
    included: [
      "Full-stack custom development",
      "Cross-platform compatibility",
      "QA & Automated Testing",
      "Dedicated Project Manager",
      "**Unlimited revisions until you've launched**"
    ],
    extras: [
      { service: "Monthly Retainer", price: "$5,000", unit: "per month" },
      { service: "DevOps Setup", price: "$1,500", unit: "one-time" },
      { service: "Auth & Security Audit", price: "$2,000", unit: "flat fee" },
      { service: "DB Migration", price: "$1,200", unit: "base price" }
    ],
    btnText: "Develop my App"
  },
  {
    id: "platforms",
    title: "Enterprise Platforms",
    desc: "Mission-critical distributed systems and planetary-scale infrastructure.",
    prices: { EUR: "€24,950+", USD: "$27,000+", AED: "99,000+ AED", AUD: "A$41,200+" },
    included: [
      "Multi-region architecture",
      "High-throughput data mesh",
      "Kubernetes orchestration",
      "Real-time analytics engine",
      "Zero-trust security protocol",
      "**24/7 Incident Response included**"
    ],
    extras: [
      { service: "Global CDN Optimization", price: "$2,000", unit: "flat fee" },
      { service: "Post-Quantum Cryptography", price: "$15,000", unit: "addon" },
      { service: "Chaos Engineering Audit", price: "$5,000", unit: "per audit" },
      { service: "Legacy System Migration", price: "$20,000", unit: "base" }
    ],
    btnText: "Architect My Platform"
  }
];

const currencies = [
  { code: 'EUR', label: 'EUR' },
  { code: 'USD', label: 'USD' },
  { code: 'AED', label: 'AED' },
  { code: 'AUD', label: 'AUD' }
];

const Pricing = () => {
  const [currency, setCurrency] = useState('EUR');
  const [expandedId, setExpandedId] = useState(null);

  const toggleExtras = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <section id="pricing" className="py-32 bg-[#0a0a0b] border-t border-zinc-900 relative">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end mb-20 gap-8">
          <div className="max-w-2xl">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tighter mb-4">
              Simple and <span className="text-zinc-500">straightforward.</span>
            </h2>
            <p className="text-zinc-500 font-light">Transparent costs for enterprise-grade engineering.</p>
          </div>
          <div className="flex p-1 bg-zinc-900/50 border border-zinc-800 rounded-full overflow-x-auto no-scrollbar">
            {currencies.map((curr) => (
              <button
                key={curr.code}
                onClick={() => setCurrency(curr.code)}
                className={`px-5 py-2 rounded-full text-xs font-mono tracking-wider transition-all whitespace-nowrap ${currency === curr.code ? 'bg-cyan-500 text-black shadow-[0_0_15px_rgba(6,182,212,0.3)] font-bold' : 'text-zinc-500 hover:text-white'}`}
              >
                {curr.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {plans.map((plan, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="bg-[#111113] border border-zinc-800/60 rounded-xl p-8 flex flex-col group hover:border-cyan-500/20 transition-all duration-500"
            >
              <h3 className="text-2xl font-medium mb-2 group-hover:text-cyan-400 transition-colors">{plan.title}</h3>
              <p className="text-zinc-400 text-sm mb-8 line-clamp-2">{plan.desc}</p>

              <div className="mb-8">
                <p className="text-zinc-500 text-xs font-semibold tracking-widest uppercase mb-1">Starts At</p>
                <div className="h-12 flex items-center">
                  <AnimatePresence mode="wait">
                    <motion.p
                      key={currency}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 10 }}
                      transition={{ duration: 0.2 }}
                      className="text-4xl font-bold font-mono tracking-tight"
                    >
                      {plan.prices[currency]}
                    </motion.p>
                  </AnimatePresence>
                </div>
              </div>

              <div className="flex-grow">
                <p className="text-zinc-400 text-sm font-medium mb-4">Core Deliverables</p>
                <ul className="space-y-3 mb-8">
                  {plan.included.map((item, i) => (
                    <li key={i} className="flex items-start text-sm text-zinc-400">
                      <span className="mr-3 text-cyan-500/50 mt-0.5">•</span>
                      {item.startsWith('**') ? (
                        <span className="font-bold text-white/90">{item.replace(/\*\*/g, '')}</span>
                      ) : (
                        <span>{item}</span>
                      )}
                    </li>
                  ))}
                </ul>

                {/* Expandable Extras Section */}
                <div className="border-t border-zinc-800/50 pt-6 mt-6 mb-8">
                  <button
                    onClick={() => toggleExtras(plan.id)}
                    className="flex items-center justify-between w-full text-zinc-400 hover:text-white transition-colors group/btn"
                  >
                    <span className="text-sm font-medium uppercase tracking-widest flex items-center gap-2">
                      <FiIcons.FiPlus className={`transition-transform duration-300 ${expandedId === plan.id ? 'rotate-45 text-cyan-400' : ''}`} />
                      Available Extras
                    </span>
                    <FiIcons.FiChevronDown className={`transition-transform duration-500 ${expandedId === plan.id ? 'rotate-180' : ''}`} />
                  </button>

                  <AnimatePresence>
                    {expandedId === plan.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="pt-6 pb-2">
                          <div className="w-full border border-zinc-800/50 rounded-lg overflow-hidden bg-black/30">
                            <table className="w-full text-left text-[11px] font-mono">
                              <thead className="bg-zinc-900/50 text-zinc-500 border-b border-zinc-800/50">
                                <tr>
                                  <th className="px-4 py-2 font-medium">SERVICE</th>
                                  <th className="px-4 py-2 font-medium text-right">COST</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-zinc-800/30">
                                {plan.extras.map((extra, i) => (
                                  <tr key={i} className="hover:bg-cyan-500/5 transition-colors group/row">
                                    <td className="px-4 py-3 text-zinc-400 group-hover/row:text-zinc-200">
                                      {extra.service}
                                      <div className="text-[9px] text-zinc-600 lowercase">{extra.unit}</div>
                                    </td>
                                    <td className="px-4 py-3 text-right text-cyan-500/80 font-bold">
                                      {extra.price}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              <button className="w-full py-4 rounded bg-cyan-500 hover:bg-cyan-400 text-black font-bold flex items-center justify-center gap-2 transition-all active:scale-[0.98] mt-auto">
                {plan.btnText}
                <SafeIcon icon={FiIcons.FiArrowRight} />
              </button>
            </motion.div>
          ))}

          {/* Custom Quote Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="bg-[#111113] border border-zinc-800/60 rounded-xl p-8 flex flex-col justify-end relative overflow-hidden min-h-[500px]"
          >
            <div className="absolute top-16 left-1/2 -translate-x-1/2 w-full flex justify-center opacity-40">
              <div className="relative w-64 h-64 border border-cyan-500/10 rounded-full flex items-center justify-center bg-cyan-500/5">
                <SafeIcon icon={FiIcons.FiLayout} className="text-6xl text-cyan-900 absolute -top-4 left-8 -rotate-12" />
                <SafeIcon icon={FiIcons.FiUsers} className="text-7xl text-cyan-500" />
                <SafeIcon icon={FiIcons.FiMessageSquare} className="text-5xl text-cyan-900 absolute bottom-4 right-12 rotate-12" />
              </div>
            </div>
            <div className="relative z-10 mt-auto pt-40">
              <h3 className="text-2xl font-bold mb-4">Looking for a custom price?</h3>
              <p className="text-zinc-400 text-sm mb-8 leading-relaxed max-w-sm">
                Feel free to book a quick discovery call to tell us about your needs and we can give you a quick quote.
              </p>
              <a
                href="#"
                className="text-cyan-400 text-sm font-medium border-b border-cyan-900 pb-1 hover:border-cyan-400 transition-colors inline-block"
              >
                Book a free discovery call
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;